/**
 * GreeksHistoryChart — volatility regime + per-strike Greeks history.
 *
 * Underlying-level modes (always available from existing `metricsHistory`):
 *   • IV vs HV  — ATM IV vs HV 30d
 *   • IVR       — IV rank in 52-week range (0-100)
 *   • GEX       — total gamma exposure
 *
 * Phase C.1 — per-strike modes (require `symbol` + `atmStrike` + `expirySeriesId`):
 *   • Delta     — per-strike Delta history (CE vs PE)
 *   • Gamma     — per-strike Gamma history (CE vs PE)
 *
 * Data for per-strike modes comes from `OptionStrikeHistory` rows
 * (written by `optionChainPolling` on every scheduled poll) via the
 * new `/options/greeks-history/:symbol` endpoint.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardHeader, CardContent, Typography, ToggleButton, ToggleButtonGroup, Stack, Skeleton } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Legend } from 'recharts';
import { useAppTheme } from '../../hooks/useAppTheme';
import { optionsService } from '../../services/options.service';

interface HistoryPoint {
  computedAt: string;
  atmIv?: number;
  ivRank52w?: number;
  hv30d?: number;
  gexTotal?: number;
}

interface Props {
  history: HistoryPoint[];
  tickFormatter: (v: any) => string;
  /** Required for per-strike modes (Delta / Gamma). When absent, those toggles are hidden. */
  symbol?: string;
  atmStrike?: number;
  expirySeriesId?: number;
}

type Metric = 'iv' | 'ivr' | 'gex' | 'delta' | 'gamma';

interface StrikePoint {
  capturedAt: string;
  iv: number | null;
  delta: number | null;
  gamma: number | null;
  ltp: number | null;
  oi: number | null;
}

const GreeksHistoryChart: React.FC<Props> = ({ history, tickFormatter, symbol, atmStrike, expirySeriesId }) => {
  const { C } = useAppTheme();
  const [metric, setMetric] = useState<Metric>('iv');
  const [cePoints, setCePoints] = useState<StrikePoint[]>([]);
  const [pePoints, setPePoints] = useState<StrikePoint[]>([]);
  const [perStrikeLoading, setPerStrikeLoading] = useState(false);

  const perStrikeReady = !!(symbol && atmStrike && atmStrike > 0);

  // Fetch per-strike history when a per-strike mode is selected.
  useEffect(() => {
    if (!perStrikeReady) return;
    if (metric !== 'delta' && metric !== 'gamma') return;
    let cancelled = false;
    setPerStrikeLoading(true);
    Promise.all([
      optionsService.getStrikeGreeksHistory(symbol!, { strike: atmStrike!, optionType: 'CE', expirySeriesId, days: 7 }).then(r => r.data?.data || []),
      optionsService.getStrikeGreeksHistory(symbol!, { strike: atmStrike!, optionType: 'PE', expirySeriesId, days: 7 }).then(r => r.data?.data || []),
    ]).then(([ce, pe]) => {
      if (cancelled) return;
      setCePoints(ce); setPePoints(pe);
    }).catch(() => {
      if (!cancelled) { setCePoints([]); setPePoints([]); }
    }).finally(() => { if (!cancelled) setPerStrikeLoading(false); });
    return () => { cancelled = true; };
  }, [metric, perStrikeReady, symbol, atmStrike, expirySeriesId]);

  // Underlying-level series (existing IV/IVR/GEX modes)
  const series = useMemo(() => history.map(p => ({
    t: p.computedAt,
    iv: p.atmIv != null ? Number(p.atmIv) : null,
    hv: p.hv30d != null ? Number(p.hv30d) : null,
    ivr: p.ivRank52w != null ? Number(p.ivRank52w) : null,
    gex: p.gexTotal != null ? Number(p.gexTotal) : null,
  })), [history]);

  // Per-strike merged series (CE + PE aligned on capturedAt)
  const perStrikeSeries = useMemo(() => {
    if (metric !== 'delta' && metric !== 'gamma') return [];
    const byTime = new Map<string, { t: string; ce: number | null; pe: number | null }>();
    const key = metric === 'delta' ? 'delta' : 'gamma';
    for (const p of cePoints) {
      const t = p.capturedAt;
      const e = byTime.get(t) || { t, ce: null, pe: null };
      e.ce = p[key];
      byTime.set(t, e);
    }
    for (const p of pePoints) {
      const t = p.capturedAt;
      const e = byTime.get(t) || { t, ce: null, pe: null };
      e.pe = p[key];
      byTime.set(t, e);
    }
    return Array.from(byTime.values()).sort((a, b) => a.t.localeCompare(b.t));
  }, [metric, cePoints, pePoints]);

  const config: Record<Metric, { label: string; dataKey: string; color: string; suffix: string; secondary?: { dataKey: string; color: string; label: string } }> = {
    iv: { label: 'ATM IV vs HV 30d', dataKey: 'iv', color: C.bear, suffix: '%', secondary: { dataKey: 'hv', color: C.neutral, label: 'HV30d' } },
    ivr: { label: 'IV Rank (52w)',    dataKey: 'ivr', color: C.warn, suffix: '' },
    gex: { label: 'Gamma Exposure',   dataKey: 'gex', color: C.accent, suffix: '' },
    delta: { label: 'Delta (ATM strike CE vs PE)', dataKey: 'ce', color: C.bull, suffix: '', secondary: { dataKey: 'pe', color: C.bear, label: 'PE Δ' } },
    gamma: { label: 'Gamma (ATM strike CE vs PE)', dataKey: 'ce', color: C.bull, suffix: '', secondary: { dataKey: 'pe', color: C.bear, label: 'PE Γ' } },
  };
  const cfg = config[metric];

  const isPerStrikeMode = metric === 'delta' || metric === 'gamma';
  const chartData = isPerStrikeMode ? perStrikeSeries : series;
  const xKey = isPerStrikeMode ? 't' : 't';
  const noData = chartData.length === 0;

  return (
    <Card>
      <CardHeader
        title={<Typography fontWeight={700}>Volatility regime & Greeks over time</Typography>}
        subheader={
          <Typography variant="caption" color="text.secondary">
            Underlying-level (IV / IVR / GEX) from metrics history. Per-strike Δ / Γ from option_strike_history (last 7 days).
            {isPerStrikeMode && atmStrike ? ` · Showing ATM strike ${atmStrike}` : ''}
          </Typography>
        }
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)}>
              <ToggleButton value="iv">IV vs HV</ToggleButton>
              <ToggleButton value="ivr">IVR</ToggleButton>
              <ToggleButton value="gex">GEX</ToggleButton>
              {perStrikeReady && <ToggleButton value="delta">Δ</ToggleButton>}
              {perStrikeReady && <ToggleButton value="gamma">Γ</ToggleButton>}
            </ToggleButtonGroup>
          </Stack>
        }
      />
      <CardContent>
        {perStrikeLoading && isPerStrikeMode ? (
          <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
        ) : noData ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {isPerStrikeMode
              ? 'No per-strike history yet — the chain poll will populate this once it runs for this expiry.'
              : 'No history yet. Will populate as the backend polls per-symbol metrics.'}
          </Typography>
        ) : (
          <Box sx={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                <XAxis dataKey={xKey} tickFormatter={tickFormatter} stroke={C.dim} fontSize={11} />
                <YAxis stroke={C.dim} fontSize={11} />
                <Tooltip
                  contentStyle={{ background: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }}
                  formatter={(value: any) => value != null ? `${Number(value).toFixed(isPerStrikeMode ? 4 : 2)}${cfg.suffix}` : '—'}
                  labelFormatter={(label) => new Date(label).toLocaleString()}
                />
                {metric === 'gex' && <ReferenceLine y={0} stroke={C.dim} />}
                {isPerStrikeMode && <ReferenceLine y={0} stroke={C.dim} strokeDasharray="2 2" />}
                {isPerStrikeMode && <Legend wrapperStyle={{ fontSize: '0.75rem' }} />}
                <Line type="monotone" dataKey={cfg.dataKey} stroke={cfg.color} dot={false} strokeWidth={2} name={isPerStrikeMode ? (metric === 'delta' ? 'CE Δ' : 'CE Γ') : cfg.label} />
                {cfg.secondary && (
                  <Line type="monotone" dataKey={cfg.secondary.dataKey} stroke={cfg.secondary.color} dot={false} strokeWidth={2} strokeDasharray={isPerStrikeMode ? undefined : '4 4'} name={cfg.secondary.label} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default GreeksHistoryChart;
